import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { defaultShapeTextParagraphs } from '@/lib/shapes/shape-text-frame'
import { extractRunStyle } from '@/lib/text/run-style'
import { blankParagraphFromExisting } from '@/lib/text/blank-paragraph'
import type { BuildLinkedShapeFrameArgs } from './types/build-linked-shape-frame-args'

/** The last run of `paragraphs`, walking backwards — the style a continuation of this content picks
 *  up from. Skips trailing paragraphs/lines that carry no runs at all. */
function findTrailingRun(paragraphs: Paragraph[]): TextRun | undefined {
  for (let p = paragraphs.length - 1; p >= 0; p -= 1) {
    const lines = paragraphs[p]!.textLines ?? []

    for (let l = lines.length - 1; l >= 0; l -= 1) {
      const runs = lines[l]!.textRuns ?? []

      if (runs.length > 0) return runs[runs.length - 1]
    }
  }

  return undefined
}

/**
 * A style-only blank paragraph carrying `sourceParagraphs`' TRAILING typography, so text typed into
 * a text-free frame continues the story's font. Used for both ends of that problem: the seed a newly
 * created linked frame starts with (below), and the placeholder a frame the reflow drained keeps in
 * place of `[]` (`sliceOrSeed` in `chain-story.ts`).
 *
 * Without a seed the frame holds no runs at all, and every downstream style resolver then lands on
 * the one hardcoded system default (AWPCHelvetica 14pt) with no way back:
 * `mountEditableParagraphBlocks` mounts `blankParagraphFromExisting()` with NO argument,
 * `writeParagraphDataAttrs` mirrors that Helvetica run onto the block, and
 * `getDestinationRunStyle`'s block-attribute recovery layer reads it straight back — so the
 * "recovery" path recovers the default itself. Text that FLOWS in was never affected (it moves as
 * real run spans carrying `data-run-font-*`); only typing into the still-empty frame was.
 *
 * The TRAILING run, not the first: the new frame continues from the END of the source.
 *
 * Returns `[]` when the source has no runs to inherit from, leaving the existing fallback chain
 * untouched rather than stamping a default of our own.
 *
 * Drop-cap metadata is deliberately NOT carried over — a drop cap decorates where a paragraph
 * STARTS, so inheriting it would turn the first character typed in the continuation frame into a
 * drop cap.
 */
export function buildLinkedFrameStyleSeed(sourceParagraphs: Paragraph[]): Paragraph[] {
  const trailingRun = findTrailingRun(sourceParagraphs)

  if (!trailingRun) return []

  const {
    dropCap: _dropCap,
    dropCapLines: _dropCapLines,
    dropCapOffset: _dropCapOffset,
    ...meta
  } = blankParagraphFromExisting(sourceParagraphs[sourceParagraphs.length - 1])

  return [
    {
      ...meta,
      textLines: [{ textRuns: [{ ...extractRunStyle(trailingRun), textContent: '' }] }],
    },
  ]
}

/**
 * Builds the new frame created by clicking empty canvas while a link started from a SHAPE acting as
 * a text frame is in progress (WMPCM-6884). The target is a real `ShapeData` clone — same kind, same
 * outline, same fill and border — rather than the plain `kindId 3` rectangle this path used to
 * produce for every source.
 *
 * **Why a shape and not a styled text frame.** The AC asks for the target's shape, fill and border
 * to match the source. A shape clone carries those fields: `buildEditorShape` persists `fill`/`stroke`/`strokeWidth`/`opacity`
 * into `editorShape` and `checkpointToShapes` reads them straight back, which is how every other
 * shape in the editor already round-trips.
 *
 * **Built as an explicit allow-list, never `...sourceShape`.** A spread would silently carry
 * `locked`, `frozen`, `photoFill`, `parentGroupId`, `fractionalIndex`, the wrap settings and — worst
 * — the source's own TEXT into the new frame. `locked`/`frozen` are set to `false` explicitly so a
 * future spread cannot start inheriting them unnoticed.
 *
 * `rotation` is deliberately 0 and the source's UNROTATED width/height are used — WMPCM-6884
 * explicitly defers rotation.
 *
 * `id` is left empty on purpose: `ShapeManager.addShapeData` mints one via `generateShapeId`, which
 * produces the dash-free 32-character hex GUID the backend requires of client-generated shape ids.
 * `crypto.randomUUID()` — correct for a text box — would be the wrong format here. Callers must use
 * the `ShapeData` that `addShapeData` RETURNS, not this one.
 *
 * `path`/`pathCommands` are copied verbatim, which is exactly right because they live in the
 * element's own local (0,0)–(width,height) space. A drag-supplied size is therefore NOT taken here:
 * the caller hands it to `ShapeManager.addShapeData`, whose size-change branch rescales the commands
 * and regenerates the SVG path (preserving open/closed state for pen paths). It must go in that
 * call and not a follow-up `updateShape` — the history snapshot is taken inside it, so a later
 * resize would leave undo/redo holding the source's dimensions.
 *
 * **`spreadId` must be carried.** Since text-unification every linkable frame is projected through
 * `projectShapeToLinkable`, so this builder runs for *every* link target, Text-tool source included. Leaving `spreadId` unset would silently weaken `canLink`'s
 * cross-spread guard, which only rejects when BOTH sides carry one.
 */
export function buildLinkedShapeFrame({
  sourceShape,
  docPoint,
  uid,
  fallbackSpreadId,
}: BuildLinkedShapeFrameArgs): ShapeData {
  const styleSeed = buildLinkedFrameStyleSeed(sourceShape.paragraphs ?? [])

  return {
    id: '',
    uid,
    type: sourceShape.type,
    ...(sourceShape.kindId !== undefined ? { kindId: sourceShape.kindId } : {}),
    x: docPoint.x,
    y: docPoint.y,
    width: sourceShape.width,
    height: sourceShape.height,
    rotation: 0,
    ...(sourceShape.path !== undefined ? { path: sourceShape.path } : {}),
    ...(sourceShape.pathCommands !== undefined ? { pathCommands: sourceShape.pathCommands } : {}),
    ...(sourceShape.fill !== undefined ? { fill: sourceShape.fill } : {}),
    ...(sourceShape.stroke !== undefined ? { stroke: sourceShape.stroke } : {}),
    ...(sourceShape.strokeWidth !== undefined ? { strokeWidth: sourceShape.strokeWidth } : {}),
    ...(sourceShape.opacity !== undefined ? { opacity: sourceShape.opacity } : {}),
    ...(sourceShape.roundedCorner !== undefined
      ? { roundedCorner: sourceShape.roundedCorner }
      : {}),
    ...(sourceShape.sides !== undefined ? { sides: sourceShape.sides } : {}),
    ...(sourceShape.inset !== undefined ? { inset: sourceShape.inset } : {}),
    // Must never be `[]`. A shape "is a text frame" purely by carrying `paragraphs`
    // (`isShapeTextFrame`), and `buildEditorShape` only writes that identity marker when the array
    // has length — so an empty one would create a frame that is dropped from its own chain by the
    // very next reflow and reloads as a plain shape. `buildLinkedFrameStyleSeed` returns `[]` only
    // when the source has no runs to inherit from, which is the one case where stamping the system
    // default is the right answer rather than a silent Helvetica regression.
    paragraphs: styleSeed.length > 0 ? styleSeed : defaultShapeTextParagraphs(),
    locked: false,
    frozen: false,
    spreadId: sourceShape.spreadId ?? fallbackSpreadId,
  }
}
